import { useCallback, useEffect, useState } from "react";
import {
  calculateFoodAccessAreas,
  fetchFoodAccessCalculationRuns,
  fetchFoodAccessCalculationVisibility,
  fetchFoodChainBranchSummary,
  fetchFoodMapSummary,
  fetchFoodOsmStoreNormalizationSummary,
  postFoodChainBranchImport,
  postFoodChainOsmReconciliation,
} from "../../../../api/foodApi";
import { FOOD_OSM_SOURCE_KEYS } from "../../../../constants/foodSourceConstants";
import { formatRelativeFreshness } from "../../../../utils/foodFormatters";

const FoodAccessAdminPage = ({ onClose }) => {
  const [summary, setSummary] = useState(null);
  const [normalizationSummary, setNormalizationSummary] = useState(null);
  const [chainSummary, setChainSummary] = useState(null);
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [pipelineResult, setPipelineResult] = useState(null);
  const [city, setCity] = useState("Medellín");
  const [department, setDepartment] = useState("Antioquia");
  const [dryRun, setDryRun] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [calculationVisibility, setCalculationVisibility] = useState(null);
  const [calculationRuns, setCalculationRuns] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    try {
      const [mapSummary, osmSummary, chain, visibility, runs] = await Promise.all([
        fetchFoodMapSummary({ city, department }),
        fetchFoodOsmStoreNormalizationSummary({ sourceKey: FOOD_OSM_SOURCE_KEYS.FOOD_POIS }),
        fetchFoodChainBranchSummary(),
        fetchFoodAccessCalculationVisibility({ city, department, areaType: "NEIGHBORHOOD" }),
        fetchFoodAccessCalculationRuns({ city, department, areaType: "NEIGHBORHOOD", limit: 5 }),
      ]);
      setSummary(mapSummary);
      setNormalizationSummary(osmSummary);
      setChainSummary(chain);
      setCalculationVisibility(visibility);
      setCalculationRuns(runs.runs || []);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.message || "Unable to load summary.");
    }
  }, [city, department]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCalculate = async () => {
    try {
      setRunning(true);
      setResult(
        await calculateFoodAccessAreas({
          city,
          department,
          areaType: "NEIGHBORHOOD",
          dryRun,
        })
      );
      if (!dryRun) await load();
    } catch (error) {
      setErrorMessage(error.message || "Calculation failed.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="admin-page food-access-admin-page">
      <header className="admin-page__header">
        <div>
          <p className="admin-page__eyebrow">Food Data</p>
          <h1>Food Access Map</h1>
          <p className="muted">Store coverage and access area scoring (Milestone 7 recalculation).</p>
        </div>
        {onClose ? (
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        ) : null}
      </header>

      {summary ? (
        <section className="panel-card">
          <h2>Data quality</h2>
          <ul>
            <li>Total stores: {summary.totalFoodStores}</li>
            <li>With coordinates: {summary.storesWithCoordinates}</li>
            <li>Access areas calculated: {summary.accessAreaCount}</li>
            <li>Low access areas: {summary.lowAccessAreas}</li>
          </ul>
          <p className="muted">
            Local tiendas and markets can be enriched from OpenStreetMap via OSM_FOOD_POIS. Use
            Admin → Source Controls (Food tab) or Food Access Map to run ingestion.
          </p>
          {normalizationSummary ? (
            <p>
              OSM normalization: {normalizationSummary.autoNormalized} trusted,{" "}
              {normalizationSummary.needsReview} need review,{" "}
              {normalizationSummary.markedDuplicates} marked duplicate.{" "}
              <a href="#/admin/food-store-review-queue">Open store review queue</a>
            </p>
          ) : null}
        </section>
      ) : null}

      <section className="panel-card">
        <h2>Access area calculations</h2>
        {calculationVisibility?.latestRun ? (
          <ul>
            <li>Latest status: {calculationVisibility.latestRun.status}</li>
            <li>Trusted stores used: {calculationVisibility.latestRun.trustedStoreCount}</li>
            <li>Areas scored: {calculationVisibility.latestRun.updatedAreaCount}</li>
            <li>
              Last calculated:{" "}
              {formatRelativeFreshness(calculationVisibility.latestCalculatedAt)}
            </li>
            {calculationVisibility.latestRun.warnings?.length ? (
              <li>Warnings: {calculationVisibility.latestRun.warnings.length}</li>
            ) : null}
            {calculationVisibility.latestRun.errors?.length ? (
              <li>Errors: {calculationVisibility.latestRun.errors.length}</li>
            ) : null}
          </ul>
        ) : (
          <p className="muted">No completed access calculation runs yet.</p>
        )}
        {calculationRuns.length ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Started</th>
                <th>Status</th>
                <th>Trusted stores</th>
                <th>Areas</th>
              </tr>
            </thead>
            <tbody>
              {calculationRuns.map((run) => (
                <tr key={run.id}>
                  <td>{formatRelativeFreshness(run.startedAt)}</td>
                  <td>{run.status}</td>
                  <td>{run.trustedStoreCount}</td>
                  <td>{run.updatedAreaCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        <p className="muted">
          One-click OSM ingest and access recalculation live on{" "}
          <a href="#/admin/food-access-operations">Food Access Operations</a>. Use the dry-run
          control below for legacy access calculation preview.
        </p>
      </section>

      <section className="panel-card">
        <div className="food-normalization-page__control-row">
          <label>
            City <input value={city} onChange={(e) => setCity(e.target.value)} />
          </label>
          <label>
            Department{" "}
            <input value={department} onChange={(e) => setDepartment(e.target.value)} />
          </label>
          <label className="food-comparison-filters__checkbox">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
            Dry run
          </label>
          <button
            type="button"
            className="btn btn--primary"
            disabled={running}
            onClick={handleCalculate}
          >
            Calculate access areas
          </button>
        </div>
      </section>

      {errorMessage ? <p className="food-dashboard__error">{errorMessage}</p> : null}

      {chainSummary ? (
        <section className="panel-card">
          <h2>Chain branch coverage (Milestone 3)</h2>
          <ul>
            <li>Chain branches imported: {chainSummary.totalChainBranches}</li>
            <li>Matched to OSM: {chainSummary.osmMatched}</li>
            <li>Chain-only (no OSM match): {chainSummary.chainOnly}</li>
            <li>Conflicts: {chainSummary.conflicts}</li>
            <li>OSM rows linked to chain: {chainSummary.osmLinkedToChain}</li>
          </ul>
          <div className="food-normalization-page__control-row">
            <button
              type="button"
              className="btn btn--ghost"
              disabled={pipelineRunning}
              onClick={async () => {
                try {
                  setPipelineRunning(true);
                  setPipelineResult(await postFoodChainBranchImport({ apply: true }));
                  await load();
                } catch (error) {
                  setErrorMessage(error.message || "Chain import failed.");
                } finally {
                  setPipelineRunning(false);
                }
              }}
            >
              Import chain branches
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={pipelineRunning}
              onClick={async () => {
                try {
                  setPipelineRunning(true);
                  setPipelineResult(await postFoodChainOsmReconciliation({ apply: true }));
                  await load();
                } catch (error) {
                  setErrorMessage(error.message || "Reconciliation failed.");
                } finally {
                  setPipelineRunning(false);
                }
              }}
            >
              Reconcile with OSM
            </button>
            <a href="#/admin/food-store-review-queue" className="btn btn--ghost">
              Open store review queue
            </a>
            <a href="#/admin/food-chain-branch-locator-strategy" className="btn btn--ghost">
              Chain branch locator strategy
            </a>
          </div>
        </section>
      ) : null}

      {pipelineResult ? (
        <pre className="food-normalization-page__summary">{JSON.stringify(pipelineResult, null, 2)}</pre>
      ) : null}
      {result ? (
        <pre className="food-normalization-page__summary">{JSON.stringify(result, null, 2)}</pre>
      ) : null}
    </div>
  );
};

export default FoodAccessAdminPage;
